import { nextTick, onUnmounted, watch, type Ref } from 'vue'

// 弹窗焦点陷阱：激活时把焦点移入容器，Tab/Shift+Tab 在容器内循环，Esc 交给调用方关闭；
// 失活或卸载时按需把焦点还给触发元素，避免键盘用户的焦点落到背景页面。

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])'

// 多个陷阱叠加（如派活弹窗上再弹确认框）时只让最上层响应按键
const stack: symbol[] = []

export interface FocusTrapOptions {
  active: Readonly<Ref<boolean>>
  onEscape?: () => void
  restoreFocus?: () => void
  // 指定首个聚焦元素；返回空则回落到容器内首个可聚焦元素，再回落到容器本身
  initialFocus?: () => HTMLElement | null | undefined
}

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) =>
      el.tabIndex >= 0 &&
      !(el as HTMLInputElement).disabled &&
      !(el instanceof HTMLInputElement && el.type === 'hidden') &&
      el.getClientRects().length > 0,
  )
}

export function useFocusTrap(
  containerRef: Readonly<Ref<HTMLElement | null | undefined>>,
  options: FocusTrapOptions,
) {
  const id = Symbol('focus-trap')
  let engaged = false

  function onKeydown(e: KeyboardEvent) {
    const root = containerRef.value
    if (!root || e.isComposing || stack[stack.length - 1] !== id) return
    if (e.key === 'Escape') {
      if (!options.onEscape || e.defaultPrevented) return
      e.preventDefault()
      e.stopPropagation()
      options.onEscape()
      return
    }
    if (e.key !== 'Tab') return
    const items = focusables(root)
    if (!items.length) {
      e.preventDefault()
      root.focus()
      return
    }
    const first = items[0]
    const last = items[items.length - 1]
    const current = document.activeElement
    // 焦点在容器本身或已跑出容器时，也拉回首/末元素
    const stray = !current || current === root || !root.contains(current)
    if (e.shiftKey ? stray || current === first : stray || current === last) {
      e.preventDefault()
      ;(e.shiftKey ? last : first).focus()
    }
  }

  function focusInitial() {
    const root = containerRef.value
    if (!engaged || !root || root.contains(document.activeElement)) return
    const target = options.initialFocus?.() ?? focusables(root)[0]
    if (target) {
      target.focus()
      return
    }
    if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1')
    root.focus()
  }

  function engage() {
    if (engaged) return
    engaged = true
    stack.push(id)
    document.addEventListener('keydown', onKeydown)
    void nextTick(focusInitial)
  }

  function release() {
    if (!engaged) return
    engaged = false
    const i = stack.indexOf(id)
    if (i >= 0) stack.splice(i, 1)
    document.removeEventListener('keydown', onKeydown)
    options.restoreFocus?.()
  }

  watch(options.active, (on) => (on ? engage() : release()), { immediate: true })
  onUnmounted(release)
}
